import React, { useState, useCallback, useMemo } from "react";
import { semantic } from "../config/theme";
import { CHART_EXPORT_BG } from "./charts/chartTheme";
import { useStatusColors } from "../helpers/statusColors";

/**
 * Lightweight, hand-drawn SVG chart of your picks vs the market-ADP line.
 * Uses viewBox for responsive sizing — no width prop or measurement needed.
 *
 * The edge is the hero: a stem connects every pick to the market-ADP line at
 * that date, and its direction/length IS the CLV (pick later than market =
 * value = green, below the line; earlier = reach = red, above it). A summary
 * strip states the story in words.
 *
 * Underdog and DraftKings are separate boards, so the chart carries one market
 * line per platform and scores every pick against ITS OWN. It used to resolve a
 * single line per player by majority vote, which measured the minority
 * platform's picks against a board they were never drafted on.
 */

export interface PickTrendPoint {
  date: number;   // epoch ms
  pick: number;
  /** Platform this pick was made on — selects which market line scores it. */
  provider?: string;
  _ghost?: boolean;
}

/** One platform's market, pre-clipped to the chart's date window. */
export interface MarketLine {
  provider: string;
  /** Short platform label for the legend and tooltip ("UD" / "DK"). */
  label: string;
  color: string;
  points: { date: number; adp: number }[];
  /** Current ADP — flat fallback when `points` has fewer than 2 in-window. */
  current?: number | null;
}

interface Props {
  data: PickTrendPoint[];
  height?: number;
  color: string;
  udAdp?: number | null;
  /** Market ADP over time per platform, epoch-ms dates, pre-filtered to the window. */
  markets?: MarketLine[];
}

// ── Layout constants ──────────────────────────────────────────────
const VB_W = 500; // internal viewBox width
const MARGIN = { top: 28, right: 12, bottom: 28, left: 34 };
const Y_MIN = 1;
// Underdog drafts 18 rounds (216 picks); DraftKings drafts 20 (240). The axis
// used to be pinned to 216, so a DraftKings pick in round 19 or 20 was plotted
// BELOW the plot floor — pick #240 landed in the date-label gutter and its
// label badge fell outside the viewBox entirely. The ceiling now follows the
// data.
const Y_MAX_UD = 216;
const Y_MAX_DK = 240;
const Y_TICK_STEP = 24;

function fmt(d: number) {
  const dt = new Date(d);
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function biWeeklyTicks(xMin: number, xMax: number): number[] {
  if (!isFinite(xMin) || !isFinite(xMax)) return [];
  const ticks: number[] = [];
  const d = new Date(xMin);
  d.setHours(0, 0, 0, 0);
  while (d.getDay() !== 1) d.setDate(d.getDate() + 1);
  while (d.getTime() <= xMax) {
    ticks.push(d.getTime());
    d.setDate(d.getDate() + 14);
  }
  return ticks;
}

/**
 * Pick marker. Platform is encoded as SHAPE, not color, because the fill is
 * already spoken for: it carries the good/bad CLV tone. Circle is the first
 * platform, diamond the second — distinguishable in greyscale and to a
 * colour-blind viewer, neither of which a second hue would survive.
 */
function Marker({ cx, cy, r, fill, shape }: {
  cx: number; cy: number; r: number; fill: string; shape: number;
}) {
  if (shape === 1) {
    const d = r * 1.25;
    return <rect x={cx - d} y={cy - d} width={d * 2} height={d * 2} fill={fill} transform={`rotate(45 ${cx} ${cy})`} />;
  }
  return <circle cx={cx} cy={cy} r={r} fill={fill} />;
}

export default function PickTrendChart({ data, height = 220, color, udAdp, markets }: Props) {
  const sc = useStatusColors();
  const [hover, setHover] = useState<number | null>(null);

  const plotW = VB_W - MARGIN.left - MARGIN.right;
  const plotH = height - MARGIN.top - MARGIN.bottom;
  const empty = plotW <= 0 || plotH <= 0 || data.length === 0;

  // Only lines with two in-window points can be drawn or interpolated; a
  // one-point platform still contributes its flat `current` price.
  const lines = useMemo(
    () => (markets ?? []).map(m => ({ ...m, points: m.points.length >= 2 ? m.points : null })),
    [markets],
  );
  const byProvider = useMemo(
    () => new Map(lines.map(m => [m.provider, m])),
    [lines],
  );

  // Grow the axis to the DraftKings board only when something actually needs
  // the room, so an Underdog-only player keeps the denser 216 scale.
  const yMax = useMemo(() => {
    let hi = Y_MAX_UD;
    for (const d of data) if (d.pick > hi) hi = d.pick;
    for (const m of lines) {
      for (const a of m.points ?? []) if (a.adp > hi) hi = a.adp;
      if (m.current != null && m.current > hi) hi = m.current;
    }
    if (udAdp != null && !isNaN(udAdp) && udAdp > hi) hi = udAdp;
    return hi > Y_MAX_UD ? Y_MAX_DK : Y_MAX_UD;
  }, [data, lines, udAdp]);

  const yTicks = useMemo(() => {
    const ticks = [Y_MIN];
    for (let v = Y_TICK_STEP; v <= yMax; v += Y_TICK_STEP) ticks.push(v);
    return ticks;
  }, [yMax]);

  // Domains
  const xMin = empty ? 0 : data[0].date;
  const xMax = empty ? 1 : data[data.length - 1].date;
  const xSpan = (xMax - xMin) || 1;

  // Map data → viewBox coords (Y reversed: pick=1 at top)
  const sx = (d: number) => MARGIN.left + ((d - xMin) / xSpan) * plotW;
  const sy = (p: number) => MARGIN.top + ((p - Y_MIN) / (yMax - Y_MIN)) * plotH;

  // Build path
  const realPoints = useMemo(() => empty ? [] : data.filter(d => !d._ghost), [empty, data]);

  // Platforms actually present among the picks, in the order the markets were
  // given. Shape encoding only kicks in for a genuinely mixed player.
  const pickProviders = useMemo(() => {
    const order = lines.map(m => m.provider);
    const seen = new Set<string>();
    for (const pt of realPoints) if (pt.provider) seen.add(pt.provider);
    const present = order.filter(p => seen.has(p));
    for (const p of seen) if (!present.includes(p)) present.push(p);
    return present;
  }, [realPoints, lines]);
  const mixed = pickProviders.length > 1;
  const shapeFor = useCallback(
    (provider?: string) => (mixed && provider ? Math.max(0, pickProviders.indexOf(provider)) : 0),
    [mixed, pickProviders],
  );

  /**
   * Market ADP for one pick, on THAT PICK'S platform. A pick whose platform has
   * no market resolves to null and draws as a bare dot — an unscored pick is
   * honest, scoring it against the other platform's board is not. The
   * `udAdp`/single-line fallbacks only apply when the pick carries no platform
   * or the chart was given no per-platform markets at all.
   */
  const marketAt = useCallback((date: number, provider?: string): number | null => {
    let m = provider ? byProvider.get(provider) : undefined;
    if (!m && (!provider || byProvider.size === 0) && lines.length === 1) m = lines[0];
    const pts = m?.points;
    if (pts) {
      if (date <= pts[0].date) return pts[0].adp;
      const last = pts[pts.length - 1];
      if (date >= last.date) return last.adp;
      for (let i = 1; i < pts.length; i++) {
        if (date <= pts[i].date) {
          const a = pts[i - 1], b = pts[i];
          const t = (date - a.date) / ((b.date - a.date) || 1);
          return a.adp + t * (b.adp - a.adp);
        }
      }
      return last.adp;
    }
    if (m && m.current != null && !isNaN(m.current)) return m.current;
    if (m) return null;
    if (provider && byProvider.size > 0) return null;
    return udAdp != null && !isNaN(udAdp) ? udAdp : null;
  }, [byProvider, lines, udAdp]);

  // Per-pick edge = your pick − market ADP at that date (positive = value).
  const edges = useMemo(
    () => realPoints.map(pt => { const m = marketAt(pt.date, pt.provider); return m == null ? null : pt.pick - m; }),
    [realPoints, marketAt],
  );
  const valid = edges.filter((e): e is number => e != null);
  const hasEdges = valid.length > 0;
  const aheadCount = valid.filter(e => e > 0).length;
  const avgEdge = valid.length ? valid.reduce((a, b) => a + b, 0) / valid.length : 0;

  // Mean edge per platform — the figure that lines up with the split columns,
  // rather than a blend across two boards.
  const edgeByProvider = useMemo(() => {
    if (!mixed) return [];
    const buckets = new Map<string, number[]>();
    realPoints.forEach((pt, i) => {
      const e = edges[i];
      if (e == null || !pt.provider) return;
      (buckets.get(pt.provider) ?? buckets.set(pt.provider, []).get(pt.provider)!).push(e);
    });
    return pickProviders
      .filter(p => buckets.has(p))
      .map(p => {
        const xs = buckets.get(p)!;
        return {
          provider: p,
          label: byProvider.get(p)?.label ?? p,
          color: byProvider.get(p)?.color,
          avg: xs.reduce((a, b) => a + b, 0) / xs.length,
        };
      });
  }, [mixed, realPoints, edges, pickProviders, byProvider]);

  // Market drift per platform, over that platform's own line.
  const drifts = useMemo(
    () => lines
      .filter(m => m.points)
      .map(m => ({
        label: m.label,
        from: m.points![0].date,
        delta: m.points![m.points!.length - 1].adp - m.points![0].adp,
      }))
      .filter(d => Math.abs(d.delta) >= 2),
    [lines],
  );

  const handleMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mx = ((e.clientX - rect.left) / rect.width) * VB_W - MARGIN.left;
    if (mx < 0 || mx > plotW || realPoints.length === 0) { setHover(null); return; }
    const dateAtMouse = xMin + (mx / plotW) * xSpan;
    let closest = 0;
    let bestDist = Infinity;
    for (let i = 0; i < realPoints.length; i++) {
      const d = Math.abs(realPoints[i].date - dateAtMouse);
      if (d < bestDist) { bestDist = d; closest = i; }
    }
    setHover(closest);
  }, [plotW, realPoints, xMin, xSpan]);

  const handleLeave = useCallback(() => setHover(null), []);

  if (empty) {
    return (
      <div style={{ width: "100%", height, display: "flex", alignItems: "center", justifyContent: "center", color: "#999", fontSize: 13 }}>
        No chart data
      </div>
    );
  }

  const drawnLines = lines.filter(m => m.points);
  const anyLine = drawnLines.length > 0;

  // Extreme picks: the only dots that get on-chart labels.
  const minPick = realPoints.length ? Math.min(...realPoints.map(p => p.pick)) : null;
  const maxPick = realPoints.length ? Math.max(...realPoints.map(p => p.pick)) : null;
  const minPt = realPoints.find(p => p.pick === minPick);
  const maxPt = realPoints.find(p => p.pick === maxPick);

  const xTicks = biWeeklyTicks(xMin, xMax);

  return (
    <div style={{ width: "100%" }}>
      {/* Summary strip — the story in plain words. Near-zero stats are hidden
          rather than printed as "−0". */}
      {hasEdges && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", fontSize: 12, marginBottom: 6, opacity: 0.82 }}>
          <span><b>{aheadCount} of {valid.length}</b> picks beat the ADP at the time</span>
          {/* Mixed portfolios get the edge PER BOARD. One blended average across
              two markets is the number that could disagree with the split
              columns sitting directly above this chart. */}
          {edgeByProvider.length > 1 ? (
            <span>
              on average{" "}
              {edgeByProvider.map((e, i) => (
                <React.Fragment key={e.provider}>
                  {i > 0 && <span style={{ opacity: 0.6 }}> · </span>}
                  <b style={{ color: e.color }}>{e.label}</b>{" "}
                  <b style={{ color: Math.abs(e.avg) < 0.5 ? undefined : e.avg >= 0 ? sc.good : sc.bad }}>
                    {Math.abs(e.avg) < 0.5 ? "on ADP" : `${Math.abs(e.avg).toFixed(0)} ${e.avg >= 0 ? "later" : "earlier"}`}
                  </b>
                </React.Fragment>
              ))}
            </span>
          ) : Math.abs(avgEdge) >= 0.5 && (
            <span>
              on average you got him{" "}
              <b style={{ color: avgEdge >= 0 ? sc.good : sc.bad }}>
                {Math.abs(avgEdge).toFixed(0)} picks {avgEdge >= 0 ? "later" : "earlier"}
              </b>{" "}
              than his ADP
            </span>
          )}
          {drifts.map(d => (
            <span key={d.label}>
              {drifts.length > 1 && <b>{d.label} </b>}
              ADP has {d.delta < 0 ? "climbed" : "slipped"}{" "}
              <b>{Math.abs(d.delta).toFixed(0)} spots</b> since {fmt(d.from)}
            </span>
          ))}
        </div>
      )}
      <svg
        viewBox={`0 0 ${VB_W} ${height}`}
        style={{ width: "100%", height: "auto", display: "block" }}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
      >
        {/* Y grid lines + labels */}
        {yTicks.map(t => (
          <g key={t}>
            <line x1={MARGIN.left} y1={sy(t)} x2={VB_W - MARGIN.right} y2={sy(t)} stroke={semantic.neutral} strokeOpacity={0.2} strokeWidth={0.5} />
            <text x={MARGIN.left - 6} y={sy(t) + 3} textAnchor="end" fontSize={7} fill={semantic.neutral}>{t}</text>
          </g>
        ))}

        {/* X tick labels */}
        {xTicks.map(t => (
          <text key={t} x={sx(t)} y={height - 6} textAnchor="middle" fontSize={7} fill={semantic.neutral}>{fmt(t)}</text>
        ))}

        {/* Current ADP reference line — only when no dated line is drawn. */}
        {udAdp != null && !isNaN(udAdp) && udAdp >= Y_MIN && udAdp <= yMax && !anyLine && (
          <line
            x1={MARGIN.left} y1={sy(udAdp)} x2={VB_W - MARGIN.right} y2={sy(udAdp)}
            stroke={semantic.neutral} strokeDasharray="5 3" strokeOpacity={0.5} strokeWidth={1}
          />
        )}

        {/* Market ADP-over-time — one translucent context line per platform.
            Still secondary to the stems and dots, but a touch less faded than
            when this was a single dark-slate line: the platform accents include
            a light-theme gold, which disappears at 0.5. */}
        {drawnLines.map(m => (
          <path
            key={m.provider}
            d={m.points!.map((pt, i) => `${i === 0 ? "M" : "L"}${sx(pt.date).toFixed(1)},${sy(pt.adp).toFixed(1)}`).join(" ")}
            fill="none" stroke={m.color} strokeWidth={2} strokeOpacity={0.65}
            strokeLinejoin="round" strokeLinecap="round"
          />
        ))}

        {/* Edge stems + picks. Stem runs from the pick to ITS OWN platform's
            market line at that date; color/length is the CLV. Shape marks the
            platform when the player was drafted on both. Latest pick gets a
            ring. */}
        {realPoints.map((pt, i) => {
          const e = edges[i];
          const cx = sx(pt.date);
          const cy = sy(pt.pick);
          const isLast = i === realPoints.length - 1;
          const shape = shapeFor(pt.provider);
          if (e == null) {
            return <Marker key={i} cx={cx} cy={cy} r={isLast ? 3.4 : 2.2} fill={color} shape={shape} />;
          }
          const tone = e > 0 ? sc.good : e < 0 ? sc.bad : semantic.neutral;
          const marketY = sy(marketAt(pt.date, pt.provider) as number);
          return (
            <g key={i}>
              <line x1={cx} y1={marketY} x2={cx} y2={cy} stroke={tone} strokeWidth={1.4} strokeOpacity={0.7} />
              <Marker cx={cx} cy={cy} r={isLast ? 4 : 2.6} fill={tone} shape={shape} />
              {isLast && <circle cx={cx} cy={cy} r={1.6} fill="#fff" />}
            </g>
          );
        })}

        {/* Label only the extremes: your earliest (best-slot) and latest picks.
            Per-dot edge labels overlapped badly on clustered picks; hover
            carries the per-pick detail instead. */}
        {minPt && (
          <g>
            <rect x={sx(minPt.date) - 12} y={sy(minPt.pick) - 16} width={24} height={12} rx={6} fill={color} />
            <text x={sx(minPt.date)} y={sy(minPt.pick) - 7} textAnchor="middle" fontSize={7} fontWeight={700} fill="#fff">
              #{minPt.pick}
            </text>
          </g>
        )}
        {maxPt && maxPt !== minPt && (
          <g>
            <rect x={sx(maxPt.date) - 12} y={sy(maxPt.pick) + 4} width={24} height={12} rx={6} fill={color} />
            <text x={sx(maxPt.date)} y={sy(maxPt.pick) + 13} textAnchor="middle" fontSize={7} fontWeight={700} fill="#fff">
              #{maxPt.pick}
            </text>
          </g>
        )}

        {/* Hover overlay: guide line, highlighted pick, tooltip. The tooltip
            flips below the dot near the top of the chart so it never clips, and
            names the platform the pick was made on — the chart's answer to
            "which board is this dot being judged against?". */}
        {hover !== null && realPoints[hover] && (() => {
          const pt = realPoints[hover];
          const e = edges[hover];
          const cx = sx(pt.date);
          const cy = sy(pt.pick);
          const tipY = cy - 26 < 2 ? cy + 10 : cy - 26;
          const platform = mixed && pt.provider ? byProvider.get(pt.provider)?.label ?? pt.provider : null;
          const label = [
            `Pick #${pt.pick}`,
            fmt(pt.date),
            platform,
            e == null ? null : `${e >= 0 ? "+" : "−"}${Math.abs(e).toFixed(0)} vs ADP`,
          ].filter(Boolean).join(" · ");
          const w = Math.max(104, label.length * 4.5 + 14);
          return (
            <g>
              <line x1={cx} y1={MARGIN.top} x2={cx} y2={MARGIN.top + plotH} stroke={color} strokeOpacity={0.3} strokeWidth={1} />
              <circle cx={cx} cy={cy} r={5} fill={e == null ? color : e > 0 ? sc.good : sc.bad} stroke="#fff" strokeWidth={1.5} />
              <rect x={Math.min(Math.max(cx - w / 2, 2), VB_W - w - 2)} y={tipY} width={w} height={18} rx={6} fill={CHART_EXPORT_BG} opacity={0.94} />
              <text x={Math.min(Math.max(cx, w / 2 + 2), VB_W - w / 2 - 2)} y={tipY + 12} textAnchor="middle" fontSize={8} fill="#fff" fontWeight={600}>
                {label}
              </text>
            </g>
          );
        })()}
      </svg>
    </div>
  );
}
